import React from 'react';
import { motion } from 'framer-motion';
import { Database, Layers, Cloud, Phone, MessageSquare, BarChart3, Sparkles } from 'lucide-react';

const sources = [
  { label: 'Data Warehouse', sublabel: 'Snowflake · BigQuery · Redshift', Icon: Database },
  { label: 'Enterprise Apps', sublabel: 'Salesforce · ServiceNow · SAP', Icon: Layers },
  { label: 'Cloud & APIs', sublabel: 'S3 · REST APIs · Event Streams', Icon: Cloud },
];

const outputs = [
  { label: 'AI Voice Agent', sublabel: 'Resolve calls automatically', Icon: Phone, color: 'from-indigo-500 to-indigo-700' },
  { label: 'Analytics Chat', sublabel: 'Ask data questions naturally', Icon: MessageSquare, color: 'from-violet-500 to-violet-700' },
  { label: 'IT Self-Service', sublabel: 'Automated ticket resolution', Icon: BarChart3, color: 'from-purple-500 to-purple-700' },
];

// Paths in a 0-100 viewBox.
// Left column right edge ≈ x=27, center circle at x=46 y=50,
// right column left edge ≈ x=73.
// Source node centres (vertically): ~28%, 50%, 72%.
const leftPaths = [
  'M 25 28 C 33 28 33 50 40 50',
  'M 25 50 L 40 50',
  'M 25 72 C 33 72 33 50 40 50',
];
const rightPaths = [
  'M 40 50 C 57 50 57 28 73 28',
  'M 40 50 L 73 50',
  'M 40 50 C 57 50 57 72 73 72',
];

const AnimatedPath = ({ d, gradient, delay }) => (
  <motion.path
    d={d}
    fill="none"
    stroke={`url(#${gradient})`}
    strokeWidth="0.6"
    strokeLinecap="round"
    initial={{ pathLength: 0, opacity: 0 }}
    animate={{ pathLength: 1, opacity: 1 }}
    transition={{ duration: 0.9, delay, ease: 'easeOut' }}
  />
);

const HeroDiagram = () => (
  <div className="relative w-full h-[300px] select-none">

    {/* SVG connection lines — uses preserveAspectRatio=none so x/y % match DOM % */}
    <svg
      className="absolute inset-0 w-full h-full"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
    >
      <defs>
        <linearGradient id="lgLeft" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#a5b4fc" stopOpacity="0.45" />
          <stop offset="100%" stopColor="#7c3aed" stopOpacity="0.85" />
        </linearGradient>
        <linearGradient id="lgRight" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#7c3aed" stopOpacity="0.85" />
          <stop offset="100%" stopColor="#a5b4fc" stopOpacity="0.45" />
        </linearGradient>
      </defs>

      {leftPaths.map((d, i) => (
        <AnimatedPath key={`l${i}`} d={d} gradient="lgLeft" delay={0.3 + i * 0.15} />
      ))}
      {rightPaths.map((d, i) => (
        <AnimatedPath key={`r${i}`} d={d} gradient="lgRight" delay={0.9 + i * 0.15} />
      ))}
    </svg>

    {/* Left column — source nodes */}
    <div className="absolute left-0 top-0 bottom-0 w-[25%] flex flex-col justify-center gap-3 z-10">
      {sources.map(({ label, sublabel, Icon }, i) => (
        <motion.div
          key={label}
          initial={{ opacity: 0, x: -14 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.12 }}
          className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-2.5 py-2.5 shadow-sm"
        >
          <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
            <Icon className="w-3.5 h-3.5 text-slate-500" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-semibold text-slate-800 leading-tight">{label}</p>
            <p className="text-[8px] text-slate-400 leading-tight">{sublabel}</p>
          </div>
        </motion.div>
      ))}
      <p className="text-[8px] font-semibold uppercase tracking-widest text-slate-400 pl-1">Data Sources</p>
    </div>

    {/* Center — AI engine */}
    <motion.div
      className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2 z-10"
      style={{ left: '35%', top: '38%' }}
      initial={{ opacity: 0, scale: 0.65 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.55, type: 'spring', stiffness: 180 }}
    >
      <div className="relative">
        {/* Glow halo */}
        <div className="absolute inset-0 bg-indigo-400/20 rounded-full blur-2xl scale-[2.5]" />
        {/* Pulse ring */}
        <motion.div
          animate={{ scale: [1, 1.55, 1], opacity: [0.35, 0, 0.35] }}
          transition={{ duration: 2.8, repeat: Infinity }}
          className="absolute inset-0 rounded-full border border-indigo-400"
        />
        {/* Main orb */}
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
          className="relative w-[72px] h-[72px] rounded-full bg-gradient-to-br from-indigo-500 to-violet-700 flex flex-col items-center justify-center shadow-2xl shadow-indigo-400/50"
        >
          <Sparkles className="w-6 h-6 text-white" />
          <span className="text-white text-[7px] font-bold tracking-widest mt-0.5">AI</span>
        </motion.div>
      </div>
      <span className="text-[9px] font-extrabold text-indigo-700 tracking-widest uppercase">Digian.ai</span>
    </motion.div>

    {/* Right column — output nodes */}
    <div className="absolute right-0 top-0 bottom-0 w-[27%] flex flex-col justify-center gap-3 z-10">
      {outputs.map(({ label, sublabel, Icon, color }, i) => (
        <motion.div
          key={label}
          initial={{ opacity: 0, x: 14 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.9 + i * 0.12 }}
          className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-2.5 py-2.5 shadow-sm"
        >
          <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${color} flex items-center justify-center flex-shrink-0`}>
            <Icon className="w-3.5 h-3.5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] font-semibold text-slate-800 leading-tight">{label}</p>
            <p className="text-[8px] text-slate-400 leading-tight">{sublabel}</p>
          </div>
        </motion.div>
      ))}
      <p className="text-[8px] font-semibold uppercase tracking-widest text-slate-400 pl-1">AI Agents</p>
    </div>
  </div>
);

export default HeroDiagram;
